import { useState } from 'react';
import { useCustom } from '@refinedev/core';
import { Link } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface UserLivestreamHistoryRow {
  livestreamId: number;
  title: string;
  status: string;
  scheduledAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  streamDurationSeconds: number;
  totalWatchedSeconds: number;
  sessionsCount: number;
  lastSeenAt: string;
  completionPercent: number;
}

interface UserLivestreamHistoryResponse {
  total: number;
  limit: number;
  offset: number;
  summary: {
    totalLivestreamsWatched: number;
    totalWatchedSecondsAllStreams: number;
    averageCompletionPercent: number;
  };
  items: UserLivestreamHistoryRow[];
}

const PAGE_SIZE = 50;

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}с`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}ч ${minutes}м`;
  }
  return `${minutes}м`;
}

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase text-muted-foreground tracking-wide">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export function UserLivestreamHistoryPanel({ userId }: { userId: number }) {
  const [offset, setOffset] = useState(0);

  const { query } = useCustom<UserLivestreamHistoryResponse>({
    url: `/admin/users/${userId}/livestream-history`,
    method: 'get',
    config: {
      query: { limit: PAGE_SIZE, offset },
    },
  });

  const data = query.data?.data;

  if (query.isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Загрузка истории просмотра трансляций...
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Пользователь не смотрел ни одной трансляции.
        </CardContent>
      </Card>
    );
  }

  const totalPages = Math.ceil(data.total / data.limit);
  const currentPage = Math.floor(offset / data.limit) + 1;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Сводка по трансляциям</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryStat
              label="Всего стримов"
              value={String(data.summary.totalLivestreamsWatched)}
            />
            <SummaryStat
              label="Суммарно просмотрено"
              value={formatDuration(data.summary.totalWatchedSecondsAllStreams)}
            />
            <SummaryStat
              label="Средний просмотр"
              value={`${data.summary.averageCompletionPercent.toFixed(1)}%`}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Трансляция</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead className="text-right">Длительность</TableHead>
                  <TableHead className="text-right">Просмотрено</TableHead>
                  <TableHead className="text-right">% эфира</TableHead>
                  <TableHead className="text-right">Сессий</TableHead>
                  <TableHead>Последняя активность</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((row) => (
                  <TableRow key={row.livestreamId}>
                    <TableCell>
                      <Link
                        to={`/livestreams/show/${row.livestreamId}`}
                        className="text-primary hover:underline font-medium text-sm"
                      >
                        {row.title}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {row.streamDurationSeconds > 0
                        ? formatDuration(row.streamDurationSeconds)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatDuration(row.totalWatchedSeconds)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.streamDurationSeconds > 0
                        ? `${row.completionPercent.toFixed(1)}%`
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.sessionsCount}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {formatDateTime(row.lastSeenAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="px-4 py-3 flex items-center justify-between border-t">
              <div className="text-xs text-muted-foreground">
                Страница {currentPage} из {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - data.limit))}
                >
                  Назад
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={offset + data.limit >= data.total}
                  onClick={() => setOffset(offset + data.limit)}
                >
                  Вперёд
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
